import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { Capacitor } from '@capacitor/core';

// Mesmo padrão de `useUserPosition.ts` (geolocationModule) — nunca resolver o
// proxy do plugin através de `await` num import estático no topo do módulo.
async function scannerModule() {
  return import('@capacitor-mlkit/barcode-scanning');
}

export type ScannerPhase =
  | 'starting'
  | 'scanning'
  | 'denied'
  | 'unsupported'
  /** O shell nativo instalado não embarcou o plugin do scanner. */
  | 'unavailable'
  | 'error';

/**
 * Teto pra `startScan`. O plugin tem um caminho no iOS que sai SEM liquidar a
 * chamada (`ios/Plugin/BarcodeScanner.swift`: `guard let webView … else
 * { return }` não chama `completion`) — sem esse teto a Promise fica pendente
 * pra sempre e a tela trava em `starting`, preta e muda.
 */
const START_TIMEOUT_MS = 12_000;

const TIMEOUT_ERROR = 'ScannerTimeoutError';

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      const err = new Error(`startScan não respondeu em ${ms}ms`);
      err.name = TIMEOUT_ERROR;
      reject(err);
    }, ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/** Motivo técnico curto — é o único canal de diagnóstico do app nativo (não há
 * Mac no fluxo de build, logo não há Safari Web Inspector no iPhone). */
function describe(err: unknown): string {
  const platform = Capacitor.getPlatform();
  if (err instanceof Error) return `${platform} · ${err.name}: ${err.message}`;
  return `${platform} · ${String(err)}`;
}

interface UseBarcodeScannerOptions {
  /** Chamado a cada QR lido (a leitura continua — quem usa decide se para,
   * chamando `stop()` ou desmontando o componente). */
  onScanned: (rawValue: string) => void;
}

interface UseBarcodeScannerResult {
  phase: ScannerPhase;
  errorMessage: string | null;
  /** Detalhe técnico da falha, pra exibir em letra miúda e o operador
   * fotografar. `null` quando não houve falha. */
  errorDetail: string | null;
  torchAvailable: boolean;
  torchOn: boolean;
  toggleTorch: () => void;
  openSettings: () => void;
  /** No app nativo isso não é usado (a câmera aparece por trás da WebView
   * transparente) e o `<video>` NÃO deve ser montado — vazio, o WebView
   * Android pinta o placeholder de mídia por cima da câmera. No web/PWA, o
   * plugin exige um `<video>` de verdade montado pra desenhar a prévia. */
  videoRef: RefObject<HTMLVideoElement>;
}

/**
 * Câmera em tela cheia com overlay React (viewfinder maior + lanterna +
 * "digitar código" — feedback Saulo 2026-07-06 comparando com Tupi/Spott).
 * Troca o `@capacitor/barcode-scanner` (UI nativa fixa, sem lanterna nem
 * como sobrepor botões) por `@capacitor-mlkit/barcode-scanning`, que deixa
 * a WebView transparente sobre a câmera — a classe `barcode-scanner-active`
 * no `<body>` (globals.css) esconde tudo menos o overlay da tela de scan.
 *
 * Toda falha de partida TEM que virar uma fase visível. Enquanto isso não era
 * verdade, qualquer rejeição inesperada deixava `phase` em `starting` e o
 * cliente encarava uma tela preta sem explicação nem saída — foi assim que o
 * scanner ficou quebrado no iOS por um mês (docs/bugs/2026-07-30-scanner-ios-
 * plugin-ausente-spm.md).
 */
export function useBarcodeScanner({ onScanned }: UseBarcodeScannerOptions): UseBarcodeScannerResult {
  const [phase, setPhase] = useState<ScannerPhase>('starting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const onScannedRef = useRef(onScanned);
  onScannedRef.current = onScanned;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let cancelled = false;
    let listenerHandle: { remove: () => Promise<void> } | null = null;

    const fail = (err: unknown, message: string) => {
      if (cancelled) return;
      setPhase('error');
      setErrorMessage(message);
      setErrorDetail(describe(err));
    };

    void (async () => {
      // O scanner é código NATIVO. Se o shell instalado não embarcou o plugin,
      // TODA chamada rejeita com UNIMPLEMENTED ("plugin is not implemented on
      // ios") — inclusive `isSupported()`. Detectar antes evita que a rejeição
      // derrube o fluxo em silêncio e permite oferecer o código manual. A
      // checagem se auto-cura: no shell que embarca o plugin ela dá `true`.
      if (Capacitor.isNativePlatform() && !Capacitor.isPluginAvailable('BarcodeScanner')) {
        if (!cancelled) setPhase('unavailable');
        return;
      }

      const { BarcodeScanner, BarcodeFormat } = await scannerModule();

      const { supported } = await BarcodeScanner.isSupported();
      if (!supported) {
        if (!cancelled) setPhase('unsupported');
        return;
      }

      let perm = await BarcodeScanner.checkPermissions();
      if (perm.camera !== 'granted' && perm.camera !== 'limited') {
        perm = await BarcodeScanner.requestPermissions();
      }
      if (perm.camera !== 'granted' && perm.camera !== 'limited') {
        if (!cancelled) setPhase('denied');
        return;
      }
      if (cancelled) return;

      document.body.classList.add('barcode-scanner-active');
      listenerHandle = await BarcodeScanner.addListener('barcodesScanned', (event) => {
        const barcode = event.barcodes[0];
        const value = barcode?.rawValue || barcode?.displayValue;
        if (value) onScannedRef.current(value);
      });

      try {
        // Web (sem plataforma nativa): o plugin exige um <video> de verdade
        // pra desenhar a prévia (lança "videoElement must be provided" sem
        // isso) — no app nativo a câmera aparece atrás da WebView
        // transparente e esse parâmetro não é usado.
        await withTimeout(
          BarcodeScanner.startScan({
            formats: [BarcodeFormat.QrCode],
            videoElement: Capacitor.isNativePlatform() ? undefined : (videoRef.current ?? undefined)
          }),
          START_TIMEOUT_MS
        );
      } catch (err) {
        fail(
          err,
          err instanceof Error && err.name === TIMEOUT_ERROR
            ? 'A câmera não respondeu.'
            : 'Não foi possível abrir a câmera.'
        );
        return;
      }
      if (cancelled) return;
      setPhase('scanning');

      const avail = await BarcodeScanner.isTorchAvailable().catch(() => ({ available: false }));
      if (!cancelled) setTorchAvailable(avail.available);
    })().catch((err) => {
      // Rede de segurança: import dinâmico, isSupported, permissões e
      // addListener ficam fora do try acima. Sem esse catch a rejeição some e
      // a fase congela em `starting` — tela preta sem motivo.
      fail(err, 'Não foi possível abrir a câmera.');
    });

    return () => {
      cancelled = true;
      void (async () => {
        try {
          const { BarcodeScanner } = await scannerModule();
          document.body.classList.remove('barcode-scanner-active');
          if (listenerHandle) await listenerHandle.remove();
          await BarcodeScanner.stopScan();
        } catch {
          // Melhor esforço — a tela já está saindo de qualquer forma.
        }
      })();
    };
  }, []);

  const toggleTorch = useCallback(() => {
    void (async () => {
      const { BarcodeScanner } = await scannerModule();
      try {
        await BarcodeScanner.toggleTorch();
        const { enabled } = await BarcodeScanner.isTorchEnabled();
        setTorchOn(enabled);
      } catch {
        // Sem lanterna disponível nesse aparelho — botão já fica escondido
        // via `torchAvailable`, isso é só um cinto-e-suspensório.
      }
    })();
  }, []);

  const openSettings = useCallback(() => {
    void (async () => {
      const { BarcodeScanner } = await scannerModule();
      await BarcodeScanner.openSettings();
    })();
  }, []);

  return {
    phase,
    errorMessage,
    errorDetail,
    torchAvailable,
    torchOn,
    toggleTorch,
    openSettings,
    videoRef
  };
}
